import React from 'react';
import { motion } from 'framer-motion';
import '../../../ui/BrandFrame/BrandFrame.css';
import { PhotosByEvent } from '../../../../types';
import { useImageLoader } from '../../../../hooks/useImageLoader';
import { useStableRandomImage } from '../../../../hooks/useStableRandomImage';
import { useBubbleAnimation } from '../../../../hooks/useElementAnimations';
import { useButtonInteraction } from '../../../../hooks/useAccessibleInteraction';
import { useAdminStore } from '../../../../stores/useAdminStore';
import { useAuthStore } from '../../../../stores/useAuthStore';
import { InlineEventActions } from '../../../admin/InlineEventActions';
import { convertDatabasePhotosToPhotos, toMediaUrl } from '../../../../utils/dataAdapter';

interface AvatarBubbleProps {
    eventName: string;
    eventData: PhotosByEvent[string];
    isOpen: boolean;
    onAvatarClick: (event: string | null) => void;
}

const AvatarBubble: React.FC<AvatarBubbleProps> = ({
    eventName,
    eventData,
    isOpen,
    onAvatarClick
}) => {
    const isAdminMode = useAdminStore(s => s.isAdminMode);
    const revalidateSessionAfterMediaError = useAuthStore((state) => state.revalidateSessionAfterMediaError);

    // Use stable randomization for consistent avatar selection during rotation period
    const stableAvatarPath = useStableRandomImage(
        convertDatabasePhotosToPhotos(eventData.photos),
        eventData.avatarPhotoId ? toMediaUrl(eventData.avatarPhotoId) : '',
        eventName,
        {
            // Enable stable randomization for optimal caching
            enabled: true,
            // Scope the cached selection to avatar use (combined with the event key).
            selectionId: 'avatar'
        }
    );

    const { imageUrl: loadedAvatarUrl, isLoading, hasError } = useImageLoader(stableAvatarPath);

    // No tap gesture: framer makes a tap-gesture element focusable, which put a dead focus
    // stop between bubbles for Tab and the TV's spatial navigation. Bubbles have no tap variant.
    const { motionProps } = useBubbleAnimation({ isOpen, enableTap: false });

    const { interactionProps } = useButtonInteraction(
        () => onAvatarClick(eventName),
        {
            ariaLabel: `${isOpen ? 'Close' : 'Open'} story for ${eventName}`,
            ariaExpanded: isOpen,
            enableTouchEvents: true,
            preventClickAfterTouch: true
        }
    );

    let bubbleImageContent: React.ReactNode = null;
    if (isLoading) {
        bubbleImageContent = <div className="avatar-loading">Loading...</div>;
    } else if (hasError) {
        bubbleImageContent = <div className="avatar-error">❌</div>;
    } else if (loadedAvatarUrl) {
        bubbleImageContent = (
            <img
                src={loadedAvatarUrl}
                alt={eventName}
                draggable="false"
                onError={revalidateSessionAfterMediaError}
            />
        );
    }

    return (
        <motion.div
            key={eventName}
            className="avatar-carousel-bubble"
            {...motionProps}
        >
            <div
                className={`avatar-bubble-inner${isOpen ? ' avatar-bubble-open' : ''}`}
                data-testid={`avatar-bubble-${eventName}`}
                {...interactionProps}
            >
                <div className="brand-frame avatar-bubble-gradient">
                    <div className="brand-frame__surface avatar-bubble-image">
                        {bubbleImageContent}
                    </div>
                </div>
                <span className="brand-frame__label avatar-bubble-username">
                    {eventName}
                </span>

                {/* Admin gear overlay */}
                {isAdminMode && eventData.event && (
                    <InlineEventActions
                        eventData={eventData}
                    />
                )}
            </div>
        </motion.div>
    );
};

// Avoid rerenders when avatar inputs are unchanged; eventData identity covers
// avatarPhotoId and the photo pool together.
export default React.memo(AvatarBubble, (prevProps, nextProps) => {
    return (
        prevProps.isOpen === nextProps.isOpen &&
        prevProps.eventName === nextProps.eventName &&
        prevProps.eventData === nextProps.eventData
    );
});
